import React from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { DotLegalCheckbox, DotLegalMultiSelect, DotLegalRadioButton, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { isTemplateCustomer } from "../../common/templateCustomerHelper";
import { useTranslation } from "../../localization/useTranslation";
import { useAddProcessingActivityPane } from "./AddProcessingActivityPane.hooks";
import { Box, RadioGroup, styled, Typography } from "@mui/material";
import { ProcessingActivityStatus } from "../processingActivities/ProcessingActivities.types";
import { ProcessingActivityStatusManagement } from "../../auth/userContextProvider/UserContextProvider.types";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import TrialUsageWarning from "../../common/components/TrialUsageWarning.tsx";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import { AddProcessingActivityPaneProps } from "./AddProcessingActivityPane.types";

const RootBox = styled(Box)(() => ({
    display: "flex",
    mb: 2,
    mt: 0,
}));

const BodyTypography = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.dark,
}));

function AddProcessingActivityPane(props: AddProcessingActivityPaneProps) {
    const { createProcessingActivity, inProgress, validation, saveModel, onModelChange, industryAreas } = useAddProcessingActivityPane();
    const { translateString, translateStatus } = useTranslation();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures } = usePlanContext();

    validation.check(saveModel);

    return (
        <PaneLayout
            header={translateString("createProcessingActivity")}
            okButtonText={translateString("create")}
            onOkClick={createProcessingActivity}
            inProgress={inProgress}
        >
            <DotLegalTextField
                autoFocus
                debounce={false}
                size="small"
                label={translateString("processingActivity")}
                value={saveModel.name}
                onChange={(name) => onModelChange({ ...saveModel, name })}
                errorText={validation?.getErrorsForDisplay("name")}
            />
            {permissions.processingActivityStatusManagement === ProcessingActivityStatusManagement.Regular && (
                <React.Fragment>
                    <RootBox>
                        <BodyTypography>{translateString("createAs")}</BodyTypography>
                    </RootBox>
                    <RadioGroup
                        row
                        value={saveModel.processingActivityStatus === ProcessingActivityStatus.Active ? 0 : 1}
                        onChange={(e) => onModelChange({ ...saveModel, processingActivityStatus: Number((e.target as HTMLInputElement).value) })}
                    >
                        <DotLegalRadioButton value={0} labelPlacement="end" label={translateStatus(ProcessingActivityStatus.Active.toString())} />
                        <DotLegalRadioButton
                            value={1}
                            labelPlacement="end"
                            label={translateStatus(ProcessingActivityStatus.Draft.toString())}
                            disabled={!dataProtectionFeatures.processingActivityStatus}
                            tooltip={!dataProtectionFeatures.processingActivityStatus ? translateString("upgradeDataProtectionPlan") : undefined}
                        />
                    </RadioGroup>
                </React.Fragment>
            )}
            {permissions.canManageCustomers && isTemplateCustomer() && (
                <React.Fragment>
                    <DotLegalMultiSelect
                        options={industryAreas}
                        chosenOptions={saveModel.industryAreas.map((x) => {
                            return x.toString();
                        })}
                        placeholder={"Brancher"}
                        label={"Brancer"}
                        onChange={(areas) => onModelChange({ ...saveModel, industryAreas: areas })}
                        noOptionsLabel={translateString("noOptions")}
                    />

                    <DotLegalCheckbox
                        label={"Mest populær?"}
                        checked={saveModel.mostPopular}
                        onChange={(mostPopular) => onModelChange({ ...saveModel, mostPopular })}
                    />
                </React.Fragment>
            )}
            {!isTemplateCustomer() && props.shouldDisplayCreateProcessingActivityWarning && (
                <TrialUsageWarning textKey={"createExceedingNumberOfProcessingActivityWarning"} marginTop={2} />
            )}
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddProcessingActivityPane });
